/**
 * DVFAnalyzer.jsx
 * ================
 * Composant réutilisable d'analyse de prix marché basé sur la base officielle
 * française DVF (Demandes de Valeurs Foncières).
 *
 * Usage :
 *   <DVFAnalyzer
 *     address="10 rue de la Paix"
 *     postalCode="75002"
 *     propertyType="apartment"
 *     surfaceM2={60}
 *     rooms={3}
 *     askingPrice={900000}
 *     onResult={(analysis) => ...}   // pour pré-remplir estimation
 *     compact                         // version condensée (pour widgets)
 *   />
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Database, TrendingUp, TrendingDown, MapPin, Sparkles,
    AlertTriangle, CheckCircle2, Loader2, Info, FileDown,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

function formatEUR(n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function confidenceBadge(level) {
    const map = {
        high: { label: 'Fiable', color: 'text-green-400 bg-green-500/10 border-green-500/20' },
        medium: { label: 'Modérée', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
        low: { label: 'Faible', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
    };
    const c = map[level] || map.low;
    return (
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${c.color}`}>
            Confiance : {c.label}
        </span>
    );
}

export default function DVFAnalyzer({
    address,
    postalCode,
    propertyType = 'apartment',
    surfaceM2,
    rooms,
    askingPrice,
    conditionScore,
    onResult,
    compact = false,
    autoRun = false,
}) {
    const [loading, setLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [error, setError] = useState(null);

    const run = async () => {
        if (!address || String(address).trim().length < 3) {
            toast.error('Adresse requise pour l\'analyse DVF');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const payload = {
                address,
                postal_code: postalCode || null,
                property_type: propertyType,
                surface_m2: surfaceM2 ? Number(surfaceM2) : null,
                rooms: rooms ? Number(rooms) : null,
                asking_price: askingPrice ? Number(askingPrice) : null,
                condition_score: conditionScore || null,
            };
            const { data } = await api.post('/dvf/analyze', payload);
            setAnalysis(data);
            if (data.error) {
                setError(data.error);
            } else if (onResult) {
                onResult(data);
            }
        } catch (e) {
            const msg = e.response?.data?.detail || 'Erreur analyse DVF';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        if (!address || String(address).trim().length < 3) {
            toast.error('Adresse requise');
            return;
        }
        try {
            const payload = {
                address,
                postal_code: postalCode || null,
                property_type: propertyType,
                surface_m2: surfaceM2 ? Number(surfaceM2) : null,
                rooms: rooms ? Number(rooms) : null,
                asking_price: askingPrice ? Number(askingPrice) : null,
                condition_score: conditionScore || null,
            };
            const res = await api.post('/dvf/avis-de-valeur-pdf', payload, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const cd = res.headers['content-disposition'] || '';
            const match = cd.match(/filename="([^"]+)"/);
            a.download = match ? match[1] : `Avis_de_valeur_DVF.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Avis de valeur DVF téléchargé');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur génération PDF');
        }
    };

    // Auto-run si demandé (ex: sur une fiche mandat). run() est une fonction async qui pose
    // l'état de chargement — pattern fetch-on-mount légitime.
    /* eslint-disable react-hooks/set-state-in-effect */
    React.useEffect(() => {
        if (autoRun && address) {
            run();
        }
    }, [autoRun, address]);
    /* eslint-enable react-hooks/set-state-in-effect */

    const stats = analysis?.market?.stats;
    const reco = analysis?.recommendation;
    const comparables = analysis?.market?.comparables || [];

    return (
        <div className="space-y-3" data-testid="dvf-analyzer">
            {!analysis && (
                <button
                    type="button"
                    onClick={run}
                    disabled={loading || !address}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600/20 to-cyan-500/20 border border-blue-500/30 hover:border-blue-400/60 transition-all text-sm font-bold text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="dvf-analyze-btn"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Analyse des ventes officielles…
                        </>
                    ) : (
                        <>
                            <Database className="w-4 h-4" />
                            Analyser avec la base DVF (gouvernement)
                        </>
                    )}
                </button>
            )}

            <AnimatePresence>
                {analysis && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-950/40 via-slate-950 to-cyan-950/20 overflow-hidden"
                        data-testid="dvf-result"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-blue-500/20 bg-blue-500/5">
                            <div className="flex items-center gap-2 min-w-0">
                                <Database className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">Analyse DVF</p>
                                    <p className="text-xs text-foreground truncate">
                                        {analysis.geocoding?.label || address}
                                    </p>
                                </div>
                            </div>
                            {reco?.confidence && confidenceBadge(reco.confidence)}
                        </div>

                        {/* Error state */}
                        {error && (
                            <div className="p-4 flex items-start gap-2 text-xs text-amber-300">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-bold mb-1">Analyse impossible</p>
                                    <p className="text-amber-300/80">{error}</p>
                                </div>
                            </div>
                        )}

                        {!error && stats && (
                            <div className="p-4 space-y-4">
                                {/* KPIs marché */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <StatTile label="Médiane €/m²" value={`${stats.median_price_m2 || '—'} €`} accent />
                                    <StatTile label="Moyenne €/m²" value={`${stats.avg_price_m2 || '—'} €`} />
                                    <StatTile label="Ventes analysées" value={stats.price_m2_count} sub={`${analysis.market.period_years} ans`} />
                                    <StatTile label="Fourchette" value={`${stats.p25_price_m2 || '—'}–${stats.p75_price_m2 || '—'}`} sub="Q1–Q3 €/m²" />
                                </div>

                                {/* Recommandation */}
                                {reco && reco.recommended_mid && (
                                    <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Sparkles className="w-3.5 h-3.5 text-[#FF914D]" />
                                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#FF914D]">Recommandation data-driven</p>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 mb-3">
                                            <PriceBand label="Basse" value={reco.recommended_low} />
                                            <PriceBand label="Centrale" value={reco.recommended_mid} highlight />
                                            <PriceBand label="Haute" value={reco.recommended_high} />
                                        </div>
                                        {reco.asking_vs_market_pct !== null && reco.asking_vs_market_pct !== undefined && (
                                            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                                                reco.asking_vs_market_pct > 10 ? 'bg-red-500/10 border border-red-500/20 text-red-300' :
                                                reco.asking_vs_market_pct < -10 ? 'bg-green-500/10 border border-green-500/20 text-green-300' :
                                                'bg-white/5 border border-white/10 text-muted-foreground'
                                            }`}>
                                                <span className="flex items-center gap-1.5 text-xs font-bold">
                                                    {reco.asking_vs_market_pct > 10 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                                                    Prix demandé vs marché
                                                </span>
                                                <span className="text-sm font-black">
                                                    {reco.asking_vs_market_pct > 0 ? '+' : ''}{reco.asking_vs_market_pct}%
                                                </span>
                                            </div>
                                        )}
                                        {reco.drop_suggested_pct && (
                                            <div className="mt-2 rounded-lg px-3 py-2 bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
                                                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                                                <span className="text-xs text-amber-200">
                                                    <span className="font-bold">Baisse recommandée : −{reco.drop_suggested_pct}%</span> pour s&apos;aligner sur le marché
                                                </span>
                                            </div>
                                        )}
                                        <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
                                            <Info className="w-3 h-3 inline mr-1" />
                                            {reco.reasoning}
                                        </p>
                                    </div>
                                )}

                                {/* Comparables */}
                                {!compact && comparables.length > 0 && (
                                    <details className="group">
                                        <summary className="flex items-center justify-between cursor-pointer px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] transition-colors">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                                Ventes comparables récentes ({comparables.length})
                                            </span>
                                            <span className="text-[10px] text-blue-400 group-open:hidden">Voir →</span>
                                            <span className="text-[10px] text-blue-400 hidden group-open:inline">Masquer</span>
                                        </summary>
                                        <div className="mt-2 space-y-1.5">
                                            {comparables.slice(0, 6).map((c, i) => (
                                                <div key={c.id_mutation || i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-black/30 border border-white/5 text-xs">
                                                    <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-foreground truncate">{c.adresse || '—'}</p>
                                                        <p className="text-[10px] text-muted-foreground">
                                                            {c.type_local} · {c.surface_reelle_bati ? `${c.surface_reelle_bati} m²` : '—'} · {c.nombre_pieces_principales || '—'} pièces · {c.date_mutation?.slice(0, 7) || '—'}
                                                        </p>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <p className="text-sm font-bold text-foreground">{formatEUR(c.valeur_fonciere)}</p>
                                                        {(c.price_m2 || (c.valeur_fonciere && c.surface_reelle_bati > 0)) && (
                                                            <p className="text-[10px] text-blue-400">{c.price_m2 || Math.round(c.valeur_fonciere / c.surface_reelle_bati)} €/m²</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}

                                {onResult && reco?.recommended_mid && (
                                    <button
                                        type="button"
                                        onClick={() => onResult(analysis)}
                                        className="w-full py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-[#681793] to-[#FF914D] text-white hover:brightness-110 transition-all flex items-center justify-center gap-2"
                                        data-testid="dvf-apply-btn"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Appliquer cette fourchette
                                    </button>
                                )}

                                {reco?.recommended_mid && (
                                    <button
                                        type="button"
                                        onClick={downloadPdf}
                                        className="w-full py-2 rounded-lg text-xs font-bold border border-[#FF914D]/40 text-[#FF914D] bg-[#FF914D]/5 hover:bg-[#FF914D]/10 transition-all flex items-center justify-center gap-2"
                                        data-testid="dvf-pdf-btn"
                                    >
                                        <FileDown className="w-3.5 h-3.5" />
                                        Télécharger l&apos;avis de valeur PDF (data.gouv.fr)
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="px-4 py-2 border-t border-white/5 bg-white/[0.02]">
                            <p className="text-[9px] text-muted-foreground">
                                Source : <span className="font-bold">DVF — Demandes de Valeurs Foncières</span> (data.gouv.fr / Etalab). Base officielle du gouvernement français.
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function StatTile({ label, value, sub, accent = false }) {
    return (
        <div className={`p-2.5 rounded-lg border ${accent ? 'bg-[#FF914D]/10 border-[#FF914D]/30' : 'bg-white/[0.03] border-white/10'}`}>
            <p className="text-[9px] text-muted-foreground font-bold uppercase tracking-wider truncate">{label}</p>
            <p className={`text-base font-black leading-none mt-1 ${accent ? 'text-[#FF914D]' : 'text-foreground'}`}>{value}</p>
            {sub && <p className="text-[9px] text-muted-foreground mt-1">{sub}</p>}
        </div>
    );
}

function PriceBand({ label, value, highlight = false }) {
    return (
        <div className={`p-2 rounded-lg text-center ${highlight ? 'bg-gradient-to-br from-[#681793]/30 to-[#FF914D]/20 border border-[#FF914D]/40' : 'bg-white/[0.03] border border-white/10'}`}>
            <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">{label}</p>
            <p className={`text-sm md:text-base font-black leading-none mt-1 ${highlight ? 'text-[#FF914D]' : 'text-foreground'}`}>
                {formatEUR(value)}
            </p>
        </div>
    );
}
